// src/store/collection/collectionStore.ts
import { create } from "zustand";
import type {
  CollectionNode,
  CollectionHierarchyQuery,
  PageContent,
} from "src/types/collection/collectionTypes";
import {
  getCollectionHierarchyApi,
  createCollectionApi,
  updateCollectionApi,
  deleteCollectionApi,
} from "src/api/collection/collectionApi";
import { normalizeCollectionPage } from "./collection.mappers";

type CollectionState = {
  // query
  query: CollectionHierarchyQuery;

  // server data
  pageData: PageContent<CollectionNode> | null;
  loading: boolean;
  error: string | null;

  // UI expand state (tree open/close)
  expandedIds: Record<number, boolean>; // collectionId -> expanded?

  // actions
  setQuery: (patch: Partial<CollectionHierarchyQuery>) => void;
  fetchHierarchy: () => Promise<void>;
  toggleExpand: (collectionId: number) => void;
  expandAllVisible: () => void;
  collapseAll: () => void;

  createCollection: (args: {
    payload: any; // CollectionUpsertPayload (순환 import 피하고 싶으면 any 말고 타입 import해도 OK)
    thumbnailFile?: File | null;
  }) => Promise<void>;

  updateCollection: (args: {
    id: number;
    payload: any;
    thumbnailFile?: File | null;
  }) => Promise<void>;

  deleteCollection: (id: number) => Promise<void>;
};

const defaultQuery: CollectionHierarchyQuery = {
  collectionName: "",
  parentCollectionId: undefined,
  depth: undefined,
  isActive: undefined,
  isFeatured: undefined,
  page: 0,
  size: 20,
  order: "displayOrder,asc",
};

export const useCollectionStore = create<CollectionState>((set, get) => ({
  query: defaultQuery,

  pageData: null,
  loading: false,
  error: null,

  expandedIds: {},

  setQuery: (patch) => set((s) => ({ query: { ...s.query, ...patch } })),

  fetchHierarchy: async () => {
    const { query } = get();
    set({ loading: true, error: null });

    try {
      const res = await getCollectionHierarchyApi(query);
      if (res.statusCode !== 1) throw new Error(res.message || "API error");

      const normalized = normalizeCollectionPage(res.data); // ✅ 여기!
      set({ pageData: normalized, loading: false });
    } catch (e: any) {
      set({
        loading: false,
        error: e?.message || "컬렉션 조회 중 오류가 발생했어요.",
      });
    }
  },

  toggleExpand: (collectionId) =>
    set((s) => ({
      expandedIds: {
        ...s.expandedIds,
        [collectionId]: !s.expandedIds[collectionId],
      },
    })),

  expandAllVisible: () => {
    const pageData = get().pageData;
    if (!pageData) return;

    const next: Record<number, boolean> = {};
    const walk = (nodes: CollectionNode[]) => {
      nodes.forEach((n) => {
        if (n.children?.length) next[n.collectionId] = true;
        if (n.children?.length) walk(n.children);
      });
    };
    walk(pageData.content);
    set({ expandedIds: next });
  },

  collapseAll: () => set({ expandedIds: {} }),

  createCollection: async ({ payload, thumbnailFile }) => {
    set({ loading: true, error: null });
    try {
      const res = await createCollectionApi(payload, thumbnailFile);
      if (res.statusCode !== 1) throw new Error(res.message || "API error");
      set({ loading: false });
      await get().fetchHierarchy();
    } catch (e: any) {
      set({ loading: false, error: e?.message || "생성 중 오류가 발생했어요." });
    }
  },

  updateCollection: async ({ id, payload, thumbnailFile }) => {
    set({ loading: true, error: null });
    try {
      const res = await updateCollectionApi(id, payload, thumbnailFile);
      if (res.statusCode !== 1) throw new Error(res.message || "API error");
      set({ loading: false });
      await get().fetchHierarchy();
    } catch (e: any) {
      set({ loading: false, error: e?.message || "수정 중 오류가 발생했어요." });
    }
  },

  deleteCollection: async (id) => {
    set({ loading: true, error: null });
    try {
      const res = await deleteCollectionApi(id);
      if (res.statusCode !== 1) throw new Error(res.message || "API error");
      set({ loading: false });
      await get().fetchHierarchy();
    } catch (e: any) {
      set({ loading: false, error: e?.message || "삭제 중 오류가 발생했어요." });
    }
  },
}));
